import React, { useEffect, useState, useCallback } from 'react';
import { opportunitiesApi } from '../api/client';

/**
 * Shows the applicable regulations for an opportunity: the FAR parts engaged at
 * each lifecycle stage PLUS the organization's supplement (HSAR/DFARS/NMCARS/etc.),
 * with the current stage highlighted. The contracting organization is auto-detected
 * from the opportunity's agency but can be overridden by the officer.
 */
export default function ApplicableRegulations({ opportunityId }: { opportunityId: string }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await opportunitiesApi.getRegulations(opportunityId);
      setData(res);
    } catch (e: any) {
      setError(e?.message || 'Could not load regulations');
    }
    setLoading(false);
  }, [opportunityId]);

  useEffect(() => { load(); }, [load]);

  const setOrg = async (org: string) => {
    setSaving(true); setError('');
    try {
      // Empty string clears the override -> back to auto-detection.
      await opportunitiesApi.update(opportunityId, { contracting_organization: org });
      await load();
    } catch (e: any) {
      setError(e?.message || 'Could not save organization');
    }
    setSaving(false);
  };

  if (loading) return <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>Loading applicable regulations…</div>;
  if (error) return <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13 }}>{error}</div>;
  if (!data) return null;

  const options: any[] = data.org_options || [];

  return (
    <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Applicable Regulations</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, color: '#6b7280' }}>Contracting Organization</label>
          <select
            value={data.organization}
            disabled={saving}
            onChange={e => setOrg(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12 }}
          >
            {options.map((o: any) => <option key={o.label} value={o.label}>{o.label}</option>)}
          </select>
          <span style={{ fontSize: 10, color: data.organization_source === 'override' ? '#7c3aed' : '#9ca3af' }}>
            {data.organization_source === 'override' ? 'manual' : 'auto'}
          </span>
        </div>
      </div>

      <div style={{ margin: '8px 0 14px', fontSize: 12, color: '#374151' }}>
        <strong>Regulatory stack:</strong> FAR (all stages) + <span style={{ padding: '2px 8px', background: '#ede9fe', color: '#5b21b6', borderRadius: 9999, fontSize: 11, fontWeight: 600 }}>{data.supplement}</span>
      </div>
      <p style={{ fontSize: 11, color: '#9ca3af', margin: '0 0 12px' }}>{data.note}</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {(data.stages || []).map((s: any) => (
          <div key={s.stage} style={{
            padding: '10px 12px', borderRadius: 6,
            border: `1px solid ${s.is_current ? '#7c3aed' : '#f3f4f6'}`,
            background: s.is_current ? '#f5f3ff' : '#fafafa',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: s.is_current ? '#5b21b6' : '#1f2937' }}>{s.label}</span>
              {s.is_current && <span style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed' }}>● CURRENT STAGE</span>}
            </div>
            {/* FAR parts with scope citation */}
            <div style={{ fontSize: 12, color: '#374151', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(s.far_parts || []).map((p: any, i: number) => (
                <span key={i} title={`Scope: ${p.cite}`}
                  style={{ padding: '2px 8px', background: '#eff6ff', color: '#1e40af', borderRadius: 4, border: '1px solid #dbeafe', whiteSpace: 'nowrap' }}>
                  {p.label} <span style={{ color: '#93c5fd', fontSize: 10 }}>({p.cite})</span>
                </span>
              ))}
            </div>
            {/* Parallel supplement parts */}
            {(s.supplement_parts && s.supplement_parts.length > 0) && (
              <div style={{ fontSize: 12, color: '#374151', display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                <span style={{ fontSize: 11, color: '#6b7280', alignSelf: 'center' }}>Supplement:</span>
                {s.supplement_parts.map((sp: string, i: number) => (
                  <span key={i} style={{ padding: '2px 8px', background: '#f5f3ff', color: '#5b21b6', borderRadius: 4, border: '1px solid #e9d5ff', whiteSpace: 'nowrap' }}>{sp}</span>
                ))}
              </div>
            )}
            {s.note && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 5 }}>{s.note}</div>}
          </div>
        ))}
      </div>
      <p style={{ fontSize: 10, color: '#9ca3af', marginTop: 10 }}>{data.note}</p>
    </div>
  );
}
